import SwitchFormsy from '@fuse/components/formsy/SwitchFormsy'
import { Typography, Icon, InputAdornment } from '@material-ui/core'
import CancelButton from 'app/components/buttons/CancelButton'
import { isEmptyOrNil } from 'libs/empty'
import PrimaryButton from 'app/components/buttons/PrimaryButton'
import ConfirmationDialog from 'app/components/ConfirmationDialog'
import DangerousButton from 'app/components/DangerousButton'
import ResponsiveDialog from 'app/components/dialog/ResponsiveDialog'
import ResponsiveDialogAction from 'app/components/dialog/ResponsiveDialogAction'
import ResponsiveDialogContent from 'app/components/dialog/ResponsiveDialogContent'
import { TextInput } from 'app/components/FormsyHelperComponent'
import MessageAlert from 'app/components/MessageAlert'
import effects from 'app/effects/effects'
import Assets from 'app/configs/assetConfigs'
import clsx from 'clsx'
import { useFormsy } from 'commons/form'
import useFormState, { actionTypes } from 'commons/useFormState'
import Formsy from 'formsy-react'
import * as R from 'ramda'
import React, { useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { makeStyles } from '@material-ui/styles'
import { BatteryIcon } from './BeaconsTableUtils'
import BeaconIdInputFormsy from 'app/components/BeaconIdInputFormsy'

export const getErrorCode = R.path(['response', 'data', 'details', 'code'])

const FORM_TYPE = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
}
// to do arg theme
const useStyles = makeStyles(() => ({
  iconBeacon: {
    width: 32,
  },
}))

const BeaconFormDialog = (props) => {
  const { isOpen, onClose, initialValues = {}, refetchData = () => {} } = props
  const classes = useStyles()
  const formRef = useRef()
  const { formatMessage } = useIntl()
  const { isPristine, onChange } = useFormsy(formRef)
  const reduxDispatch = useDispatch()
  const [formState, dispatch] = useFormState()
  const [beaconMetric, setBeaconMetric] = useState({})
  const [isOpenConfirmDelete, setIsOpenConfirmDelete] = useState(false)
  const [isOpenConfirmLeave, setIsOpenConfirmLeave] = useState(false)
  const [isValidBeaconId, setIsValidBeaconId] = useState(
    Boolean(initialValues.physicalAddress),
  )
  const formType = R.isEmpty(initialValues)
    ? FORM_TYPE.CREATE
    : FORM_TYPE.UPDATE
  const isFormTypeCreate = formType === FORM_TYPE.CREATE
  const isFormTypeUpdate = formType === FORM_TYPE.UPDATE
  const asyncFunction = {
    [FORM_TYPE.CREATE]: effects.apis.createBeacons,
    [FORM_TYPE.UPDATE]: effects.apis.updateBeacons,
  }[formType]

  const onValidateBeaconPhysicalAddressComplete = (value) => {
    setBeaconMetric(value)
  }

  useEffect(() => {
    if (initialValues.physicalAddress) {
      setIsValidBeaconId(true)
    }
  }, [initialValues.physicalAddress])

  const handleDelete = async () => {
    dispatch({
      type: actionTypes.SUBMIT_INIT,
    })
    try {
      await effects.apis.deleteBeacons([initialValues.id])
      await refetchData()
      setIsOpenConfirmDelete(false)
      onClose()
    } catch (error) {
      reduxDispatch(MessageAlert.showNetworkError(formatMessage)(error))
    } finally {
      dispatch({
        type: actionTypes.SUBMIT_SUCCESS,
      })
    }
  }

  const handleSubmit = async () => {
    const model = formRef.current.getModel()
    dispatch({
      type: actionTypes.SUBMIT_INIT,
    })
    try {
      const data = {
        ...beaconMetric,
        ...model,
        isActive: Number(model.isActive),
        physicalId: model.physicalAddress,
        id: initialValues.id,
      }
      await asyncFunction(data)
      await refetchData()
      onClose()
    } catch (error) {
      reduxDispatch(MessageAlert.showNetworkError(formatMessage)(error))
    } finally {
      dispatch({
        type: actionTypes.SUBMIT_SUCCESS,
      })
    }
  }
  // to do arg value
  const handleInvalid = () => {
    dispatch({
      type: actionTypes.INVALID_FORM,
    })
  }

  const handleValid = () => {
    dispatch({
      type: actionTypes.VALID_FORM,
    })
  }

  const currentValues = (formRef.current && formRef.current.getModel()) || {}

  const handleClose = () => {
    if (isFormTypeCreate) {
      const someFieldHasData = Object.values(currentValues).some(Boolean)
      return someFieldHasData ? setIsOpenConfirmLeave(true) : onClose()
    }

    return isPristine ? onClose() : setIsOpenConfirmLeave(true)
  }
  return (
    <Formsy
      onChange={onChange}
      onValidSubmit={handleSubmit}
      onValid={handleValid}
      onInvalid={handleInvalid}
      className="flex flex-col overflow-hidden"
      ref={formRef}
    >
      <ResponsiveDialog
        open={isOpen}
        onClose={handleClose}
        scroll="paper"
        fullWidth
        maxWidth="xs"
        aria-labelledby="scroll-dialog-title"
        removeAllSpace
      >
        <ResponsiveDialogContent classes={{ root: 'pt-24' }} id="beaconFormsy">
          <div className="flex justify-between mb-8 items-center">
            <Typography variant="h6">
              <FormattedMessage
                id={
                  {
                    [FORM_TYPE.CREATE]: 'beacons.form.create.title',
                    [FORM_TYPE.UPDATE]: 'beacons.form.update.title',
                  }[formType]
                }
              />
            </Typography>
            <Icon
              className="cursor-pointer"
              onClick={handleClose}
              color="action"
            >
              close
            </Icon>
          </div>
          <div
            className={clsx('flex mb-24 ltr:pr-12 rtl:pl-12', {
              'justify-between': isFormTypeUpdate,
              'justify-end': isFormTypeCreate,
            })}
          >
            {isFormTypeUpdate && (
              <div className="flex items-center">
                <img
                  className={classes.iconBeacon}
                  alt={initialValues.name}
                  src={
                    Assets.manageBeacon.colors[initialValues.color] ||
                    Assets.manageBeacon.colors.default
                  }
                />
                <Typography className="font-bold ltr:ml-4 rtl:mr-4 truncate max-w-256">
                  {currentValues.name}
                </Typography>
              </div>
            )}
            <SwitchFormsy
              name="isActive"
              className="mt-4"
              value={initialValues.isActive}
              labelPlacement="start"
              inputProps={{
                'data-testid': 'isActive',
              }}
              label={
                <Typography className={clsx('ltr:mr-8 rtl:ml-8 font-bold')}>
                  <FormattedMessage
                    id={
                      currentValues.isActive
                        ? 'beacons.table.form.isActive.on'
                        : 'beacons.table.form.isActive.off'
                    }
                  />
                </Typography>
              }
            />
          </div>
          <div className="flex">
            <TextInput
              labelId="beacons.table.form.name"
              containerClassName="flex-1 mb-24"
              placeholderId="beacons.table.form.name"
              name="name"
              inputProps={{
                'data-testid': 'name',
              }}
              value={initialValues.name}
              required
              maxLength={40}
              fullWidth
            />
          </div>
          <div className="flex">
            <TextInput
              labelId="beacons.table.form.location"
              containerClassName="flex-1 mb-24"
              placeholderId="beacons.table.form.location"
              inputProps={{
                'data-testid': 'location',
              }}
              name="location"
              value={initialValues.location}
              required
              maxLength={40}
              fullWidth
            />
          </div>
          <div className="flex">
            <TextInput
              labelId="beacons.table.form.description"
              containerClassName="flex-1 mb-24"
              placeholderId="beacons.table.form.description"
              name="description"
              multiline
              rows={5}
              inputProps={{
                'data-testid': 'description',
              }}
              maxLength={200}
              value={initialValues.description}
              fullWidth
            />
          </div>
          <div className="flex">
            <BeaconIdInputFormsy
              validations="isLength:32"
              validationError={formatMessage({
                id: 'beacons.form.error.physicalAddress.length',
              })}
              onInvalid={() => setIsValidBeaconId(false)}
              onValid={() => setIsValidBeaconId(true)}
              onValidateComplete={onValidateBeaconPhysicalAddressComplete}
              labelId="beacons.table.form.physicalAddress"
              containerClassName="flex-1 mb-24"
              placeholderId="beacons.table.form.physicalAddress"
              InputProps={{
                'data-testid': 'physicalAddress',
              }}
              name="physicalAddress"
              required
              value={initialValues.physicalAddress}
              disabled={isFormTypeUpdate}
              variant="outlined"
              fullWidth
            />
          </div>
          {isFormTypeUpdate && (
            <div className="flex">
              <TextInput
                labelId="beacons.table.form.notifications"
                containerClassName="flex-1 mb-24 ltr:pr-4 rtl:pl-4"
                placeholderId="beacons.table.form.notifications"
                name="numberNotifications"
                disabled
                value={
                  isEmptyOrNil(initialValues.alerts)
                    ? '0'
                    : initialValues.alerts.length
                }
                fullWidth
              />
              <TextInput
                labelId="beacons.table.form.battery"
                containerClassName="flex-1 mb-24 ltr:pl-4 rtl:pr-4"
                placeholderId="beacons.table.form.battery"
                name="numberNotifications"
                type="number"
                disabled
                value={' '}
                placeholder=""
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BatteryIcon
                        original={{
                          batteryPercentage: initialValues.batteryPercentage,
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />
            </div>
          )}
        </ResponsiveDialogContent>
        <ResponsiveDialogAction
          StartButton={
            <CancelButton onClick={handleClose} className="w-full" />
          }
          EndButton={
            <PrimaryButton
              isLoading={formState.isSubmitting}
              labelId={isFormTypeCreate ? 'action.add' : 'action.save'}
              disabled={
                !formState.isFormValid ||
                formState.isSubmitting ||
                isPristine ||
                !isValidBeaconId
              }
              onClick={handleSubmit}
              data-testid="submit"
              className="w-full"
            />
          }
        />
      </ResponsiveDialog>
      <ConfirmationDialog
        open={isOpenConfirmDelete}
        title={formatMessage({ id: 'beacons.delete.title' })}
        body={formatMessage({ id: 'beacons.delete.description' })}
        CancelButton={
          <CancelButton
            onClick={() => {
              setIsOpenConfirmDelete(false)
            }}
          />
        }
        ConfirmButton={
          <DangerousButton
            onClick={handleDelete}
            isLoading={formState.isSubmitting}
          >
            <FormattedMessage id="action.delete" />
          </DangerousButton>
        }
      />
      <ConfirmationDialog
        open={isOpenConfirmLeave}
        title={formatMessage({ id: 'beacons.form.leave.title' })}
        body={formatMessage({
          id: {
            [FORM_TYPE.CREATE]: 'beacons.form.leave.description',
            [FORM_TYPE.UPDATE]: 'beacons.form.leave.update.description',
          }[formType],
        })}
        CancelButton={
          <CancelButton
            onClick={() => {
              setIsOpenConfirmLeave(false)
            }}
          />
        }
        ConfirmButton={
          <PrimaryButton
            onClick={() => {
              setIsOpenConfirmLeave(false)
              onClose()
            }}
          >
            <FormattedMessage id="action.ok" />
          </PrimaryButton>
        }
      />
    </Formsy>
  )
}

export default BeaconFormDialog
